import { getDocument } from '@atlaskit/browser-apis';

import {
	WS_CLIENT_MAX_RETRY_AFTER,
	WS_CLIENT_RATE_LIMIT_SPREAD,
	WS_CLOSE_RATE_LIMITED,
} from '../../constants';

export type RetryWait = {
	wait: (retries: number) => Promise<void>;
	/**
	 * Ends the current wait. Returns true when a wait was running so callers can skip
	 * `terminate()` — graphql-ws emits 4499 from terminate during `retryWait` without ending it.
	 */
	abort: () => boolean;
};

/** Capped exponential backoff with the same jitter shape as graphql-ws. */
export const createRetryWait = (maxRetryWait: number): RetryWait => {
	let abortCurrent: (() => void) | undefined;

	return {
		abort: () => {
			if (!abortCurrent) {
				return false;
			}
			abortCurrent();
			abortCurrent = undefined;
			return true;
		},
		wait: async (retries: number): Promise<void> => {
			const jitter = Math.floor(Math.random() * 2700 + 300);
			const delay = Math.min(1000 * 2 ** retries + jitter, maxRetryWait);
			await new Promise<void>((resolve) => {
				const timer = setTimeout(() => {
					abortCurrent = undefined;
					resolve();
				}, delay);
				abortCurrent = () => {
					clearTimeout(timer);
					resolve();
				};
			});
		},
	};
};

// The library's own default `shouldRetry`. From 5.16, `Client.terminate()` emits a close-like
// `Error`, so the close shape must be checked before the type.
const isLikeCloseEvent = (value: unknown): value is { code: unknown; reason: unknown } =>
	typeof value === 'object' && value !== null && 'code' in value && 'reason' in value;

/**
 * graphql-ws retries closes by default, but stops on bare `socket.onerror` events.
 * Retry non-Error failures too so a failed socket can reconnect.
 */
export const shouldRetryTransportFailure = (errOrCloseEvent: unknown): boolean =>
	isLikeCloseEvent(errOrCloseEvent) || !(errOrCloseEvent instanceof Error);

export type RateLimitedRetryWait = RetryWait & {
	/** Give it every socket close; a rate-limit close holds the next connect until its deadline. */
	recordClose: (event: unknown) => void;
};

/** Retry-after in ms of a rate-limit close, or undefined for any other close. */
const getRateLimitRetryAfter = (event: unknown): number | undefined => {
	if (
		!isLikeCloseEvent(event) ||
		event.code !== WS_CLOSE_RATE_LIMITED ||
		typeof event.reason !== 'string' ||
		!/^\d+$/.test(event.reason)
	) {
		return undefined;
	}
	return Math.min(Math.max(Number(event.reason), 1) * 1000, WS_CLIENT_MAX_RETRY_AFTER);
};

/**
 * `createRetryWait`, but no connect starts before the retry-after of the latest rate-limit close.
 * graphql-ws resets its retry count on every acknowledgement, so the deadline is kept here.
 * `abort` cannot cut a wait short of that deadline.
 */
export const createRateLimitedRetryWait = (maxRetryWait: number): RateLimitedRetryWait => {
	let notBefore = 0;
	let abortCurrent: (() => void) | undefined;

	return {
		recordClose: (event: unknown): void => {
			const retryAfter = getRateLimitRetryAfter(event);
			if (retryAfter !== undefined) {
				notBefore =
					Date.now() + retryAfter + Math.floor(Math.random() * WS_CLIENT_RATE_LIMIT_SPREAD);
			}
		},
		abort: () => {
			if (!abortCurrent) {
				return false;
			}
			abortCurrent();
			return true;
		},
		wait: async (retries: number): Promise<void> => {
			const jitter = Math.floor(Math.random() * 2700 + 300);
			// Jitter after the cap, so clients that reached it do not retry in lockstep.
			const backoff = Math.min(1000 * 2 ** retries, maxRetryWait) + jitter;
			await new Promise<void>((resolve) => {
				let timer: ReturnType<typeof setTimeout> | undefined;
				const finish = (): void => {
					clearTimeout(timer);
					abortCurrent = undefined;
					resolve();
				};
				const finishAfter = (delay: number): void => {
					clearTimeout(timer);
					timer = setTimeout(finish, delay);
				};
				finishAfter(Math.max(backoff, notBefore - Date.now()));
				abortCurrent = () => {
					const untilDeadline = notBefore - Date.now();
					if (untilDeadline > 0) {
						finishAfter(untilDeadline);
					} else {
						finish();
					}
				};
			});
		},
	};
};

/**
 * Aborts an in-flight backoff, or calls terminate(), on online/visible when disconnected;
 * always on bfcache restore and resume.
 */
export const addRecoveryListeners = ({
	terminate,
	isConnected,
	abortRetryWait,
}: {
	terminate: () => void;
	isConnected: () => boolean;
	abortRetryWait: () => boolean;
}): (() => void) => {
	const doc = getDocument();

	const reconnect = (): void => {
		if (abortRetryWait()) {
			return;
		}
		terminate();
	};

	const wakeIfDisconnected = (): void => {
		if (!isConnected()) {
			reconnect();
		}
	};

	const onVisibilityChange = (): void => {
		if (doc?.visibilityState === 'visible') {
			wakeIfDisconnected();
		}
	};

	const onPageShow = (event: PageTransitionEvent): void => {
		// pageshow also fires on ordinary loads; only `persisted` is bfcache.
		if (event.persisted) {
			reconnect();
		}
	};

	const onResume = (): void => {
		reconnect();
	};

	window.addEventListener('online', wakeIfDisconnected);
	window.addEventListener('pageshow', onPageShow);
	doc?.addEventListener('visibilitychange', onVisibilityChange);
	// Chrome tab freeze; no pageshow.
	doc?.addEventListener('resume', onResume);

	return () => {
		window.removeEventListener('online', wakeIfDisconnected);
		window.removeEventListener('pageshow', onPageShow);
		doc?.removeEventListener('visibilitychange', onVisibilityChange);
		doc?.removeEventListener('resume', onResume);
	};
};
